import {
  Component,
  ContentChild,
  TemplateRef,
  ViewEncapsulation,
  effect,
  input,
  model,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/** Elemento ordenable de la grilla. */
export interface DashboardGrillaItem {
  id: string;
  /** `ancho` ocupa dos columnas; `completo`, toda la fila. */
  ancho?: 'normal' | 'ancho' | 'completo';
}

/**
 * Grilla ordenable de paneles de dashboard. Guarda el orden elegido por el
 * usuario en localStorage con `claveOrden` y lo aplica la primera vez que
 * recibe los elementos. Cada elemento se dibuja con el `ng-template` proyectado.
 *
 * @example
 * <app-dashboard-grilla #grilla [(items)]="graficos" claveOrden="dashboard-x">
 *   <ng-template let-grafico>
 *     <app-dashboard-panel [titulo]="grafico.titulo">…</app-dashboard-panel>
 *   </ng-template>
 * </app-dashboard-grilla>
 * <button (click)="grilla.restaurarOrden()">Restaurar orden</button>
 */
@Component({
  selector: 'app-dashboard-grilla',
  imports: [
    NgTemplateOutlet,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './dashboard-grilla.component.html',
  styleUrls: ['./dashboard-grilla.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class DashboardGrillaComponent<T extends DashboardGrillaItem> {
  public items = model.required<T[]>();
  public claveOrden = input.required<string>();

  @ContentChild(TemplateRef) public plantilla: TemplateRef<{ $implicit: T }>;

  /** Orden original, el de la primera vez que llegan los elementos. */
  private original: T[] | null = null;

  constructor() {
    effect(() => {
      const items = this.items();
      if (this.original || !items?.length) return;
      this.original = [...items];
      const ordenados = this.aplicarOrdenGuardado(items);
      if (ordenados !== items) this.items.set(ordenados);
    });
  }

  public soltar(event: CdkDragDrop<T[]>): void {
    const items = [...this.items()];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.items.set(items);
    try {
      localStorage.setItem(
        this.claveOrden(),
        JSON.stringify(items.map((i) => i.id)),
      );
    } catch {
      // Sin almacenamiento el orden dura hasta recargar
    }
  }

  public restaurarOrden(): void {
    try {
      localStorage.removeItem(this.claveOrden());
    } catch {
      // Nada que borrar
    }
    if (this.original) this.items.set([...this.original]);
  }

  private aplicarOrdenGuardado(items: T[]): T[] {
    let orden: string[];
    try {
      orden = JSON.parse(localStorage.getItem(this.claveOrden()) ?? 'null');
    } catch {
      return items;
    }
    if (!Array.isArray(orden)) return items;
    const porId = new Map(items.map((i) => [i.id, i]));
    const ordenados = orden.map((id) => porId.get(id)).filter((i) => !!i);
    // Los elementos nuevos que no estaban en el orden guardado van al final
    return [...ordenados, ...items.filter((i) => !orden.includes(i.id))];
  }
}
